import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { flattenTree } from 'components/HierarchyViewSelect/HierarchyViewSelect';
import { ModalDialog } from 'components/ModalDialog';
import type {
    WorkOrderChangeObjectItem,
    WorkOrderObjectData,
    WorkOrderPriceItem,
    WorkOrderVersionSeasonData,
} from 'gql/graphql';
import { groupByParentId } from 'pages/Project/Common/WorkOrderStructure/types';
import { useOrderSummaryTableColumns } from 'pages/Project/UerProject/WorkOrder/WorkOrderSummaryTableNew/hooks';

import { useCallback, useState, useMemo } from 'react';
import type React from 'react';

import { useWorkOrderObjectTotalColumns } from '../../ObjectTotals/hooks/useWorkOrderObjectTotalColumns';
import { useUerWorkOrderVersionObjectTotals } from '../hooks/useUerWorkOrderVersionObjectTotals';

import Style from './WorkOrderVersionObjectTotals.module.css';

interface IWorkOrderObjectTotals {
    onClose: () => void;
    rowData?: WorkOrderPriceItem | null;
    costRounding: number;
    seasons: WorkOrderVersionSeasonData[];
    workOrderObjects: WorkOrderObjectData[] | null | undefined;
    versionId: number;
}

const getRowId = ({ data }: { data: WorkOrderObjectData | WorkOrderChangeObjectItem }) => String(data.id);

export const WorkOrderVersionObjectTotals: React.FC<IWorkOrderObjectTotals> = (props) => {
    const { onClose, rowData, costRounding, seasons, workOrderObjects, versionId } = props;

    const [workOrderObject, setWorkOrderObject] = useState<WorkOrderObjectData>();

    const title = useMemo(() => {
        if (!workOrderObject) {
            return 'Итоги объекта и подобъектов строительства';
        }

        return `Итоги ${workOrderObject.parentId ? 'подобъекта' : 'объекта'} строительства «${workOrderObject.name}»`;
    }, [workOrderObject]);

    const actualItems = useMemo(() => {
        const id = rowData?.itemId;

        if (!workOrderObjects || !Array.isArray(workOrderObjects)) {
            return [];
        }

        return [
            workOrderObjects?.find((i: { id: number }) => i.id === id),
            ...(flattenTree(groupByParentId(workOrderObjects as any) as any, String(id)) as any[]),
        ];
    }, [rowData?.itemId, workOrderObjects]);

    const { uerWorkOrderVersionObjectTotalsData, uerWorkOrderObjectTotalsMakeRequest } =
        useUerWorkOrderVersionObjectTotals();

    const detailsItems = uerWorkOrderVersionObjectTotalsData?.uerWorkOrderVersionObjectTotals?.data;

    const onExpandDetailsClick = useCallback(
        (data) => {
            setWorkOrderObject(data);
            uerWorkOrderObjectTotalsMakeRequest(versionId, data.id);
        },
        [uerWorkOrderObjectTotalsMakeRequest, versionId],
    );

    const onCloseLocal = useCallback(() => {
        if (workOrderObject) {
            setWorkOrderObject(undefined);
        } else {
            onClose();
        }
    }, [onClose, workOrderObject]);

    const columns = useWorkOrderObjectTotalColumns({ onExpandDetailsClick, actualItems });

    const { columns: detailsColumns } = useOrderSummaryTableColumns({
        instanceId: String(versionId),
        costRounding,
        workOrderSeasons: seasons,
    });

    return (
        <ModalDialog
            isOpen
            title={title}
            onClose={onCloseLocal}
            dockedToWindow="bottom"
            withTitleCloseButton
            isOnClickOutsideDisable
            withBottomButtons={false}
        >
            <div className={workOrderObject ? Style.workOrderObjectsTotalDetails : Style.workOrderObjectsTotal}>
                <AgGridTable
                    columnDefs={(workOrderObject ? detailsColumns : columns) as any}
                    rowData={(workOrderObject ? detailsItems : actualItems) as any}
                    getRowId={getRowId}
                />
            </div>
        </ModalDialog>
    );
};

WorkOrderVersionObjectTotals.displayName = 'WorkOrderVersionObjectTotals';
